import React, { useState, useMemo } from "react";
import styled from "@emotion/styled";
import {
    color_text_default,
    color_text_light,
    color_colors_ocean,
    color_colors_growth,
    color_colors_decline,
    color_defaults_white,
    color_shades_dark,
    color_shades_light,
    color_shades_lightest,
    color_variants_ocean_light_1,
} from "../../../constants/colors";
import OrganizationContainer from "../../../state/containers/OrganizationContainer";

interface OrganizationSelectorProps {
    organizationContainer: OrganizationContainer;
    selectedOrgIds: string[];
    selectedOrgNames: Record<string, string>;
    onAddOrg: (orgId: string, orgName: string) => void;
    onRemoveOrg: (orgId: string) => void;
}

const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
    organizationContainer,
    selectedOrgIds,
    selectedOrgNames,
    onAddOrg,
    onRemoveOrg,
}) => {
    const [searchTerm, setSearchTerm] = useState("");

    const availableOrgs = useMemo(() => {
        const selectedSet = new Set(selectedOrgIds);
        return (organizationContainer.state.organizations || [])
            .filter((org) => org.id && org.name && !selectedSet.has(org.id))
            .filter((org) => {
                if (!searchTerm.trim()) return true;
                return org.name.toLowerCase().includes(searchTerm.toLowerCase());
            })
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [organizationContainer.state.organizations, selectedOrgIds, searchTerm]);

    return (
        <Wrapper>
            <SectionTitle>Organizations to Track</SectionTitle>

            <AddSection>
                <SearchInput
                    type="text"
                    placeholder="Search organizations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <OrgList>
                    {availableOrgs.map((org) => (
                        <OrgOption key={org.id} onClick={() => {
                            onAddOrg(org.id, org.name);
                            setSearchTerm("");
                        }}>
                            <OrgOptionName>{org.name}</OrgOptionName>
                            <AddIcon>+</AddIcon>
                        </OrgOption>
                    ))}
                    {availableOrgs.length === 0 && searchTerm && (
                        <MoreText>No matching organizations found.</MoreText>
                    )}
                </OrgList>
            </AddSection>

            {selectedOrgIds.length > 0 && (
                <SelectedSection>
                    <SelectedTitle>Selected ({selectedOrgIds.length})</SelectedTitle>
                    {selectedOrgIds.map((orgId) => (
                        <SelectedOrg key={orgId}>
                            <SelectedOrgName>{selectedOrgNames[orgId] || orgId}</SelectedOrgName>
                            <RemoveButton onClick={() => onRemoveOrg(orgId)}>Remove</RemoveButton>
                        </SelectedOrg>
                    ))}
                </SelectedSection>
            )}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
    margin: 0 0 12px 0;
    color: ${color_text_default};
    font-size: 16px;
`;

const AddSection = styled.div`
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_dark};
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 12px;
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 8px 12px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    font-size: 14px;
    margin-bottom: 8px;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
        box-shadow: 0 0 0 3px ${color_variants_ocean_light_1};
    }
`;

const OrgList = styled.div`
    max-height: 200px;
    overflow-y: auto;
`;

const OrgOption = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 8px;
    cursor: pointer;
    border-radius: 4px;

    &:hover {
        background: ${color_shades_lightest};
    }
`;

const OrgOptionName = styled.span`
    font-size: 14px;
    color: ${color_text_default};
`;

const AddIcon = styled.span`
    color: ${color_colors_growth};
    font-size: 18px;
    font-weight: bold;
`;

const MoreText = styled.div`
    font-size: 12px;
    color: ${color_text_light};
    padding: 6px 8px;
    font-style: italic;
`;

const SelectedSection = styled.div`
    margin-top: 12px;
`;

const SelectedTitle = styled.div`
    font-size: 12px;
    font-weight: 500;
    color: ${color_text_light};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 8px;
`;

const SelectedOrg = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 12px;
    background: ${color_defaults_white};
    border: 1px solid ${color_shades_light};
    border-radius: 4px;
    margin-bottom: 4px;
`;

const SelectedOrgName = styled.span`
    font-size: 14px;
    color: ${color_text_default};
`;

const RemoveButton = styled.button`
    background: ${color_colors_decline};
    color: ${color_defaults_white};
    border: none;
    padding: 4px 10px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;

    &:hover {
        opacity: 0.9;
    }
`;

export default OrganizationSelector;
